import React, { createContext, useContext, useState, useEffect } from 'react';
import { useSearchParams, useLocation } from 'react-router-dom';
import axios from 'axios';
import Papa from 'papaparse';

export const getCsvUrl = (season, gender) => {
  const baseUrls = {
    '2026': {
      male: 'https://docs.google.com/spreadsheets/d/e/2PACX-1vQrTuUARJsDO65-Qfq3j_-Ga_x4TNXiZUqocY2Y0BpbbrrSAhY0bfzSA3TCP2qVztsB4s4cC4bIiCKa/pub?gid=1290048305&output=csv',
      female: 'https://docs.google.com/spreadsheets/d/e/2PACX-1vQrTuUARJsDO65-Qfq3j_-Ga_x4TNXiZUqocY2Y0BpbbrrSAhY0bfzSA3TCP2qVztsB4s4cC4bIiCKa/pub?output=csv',
    },
    '2025': {
      male: 'https://docs.google.com/spreadsheets/d/e/2PACX-1vQo5-YqMdciz9mB0xICnp1_f3oyodh3Rdoykyicjfd2qlIn8fzUT9vgWNl-ERBqiyLb80Rgg6mZwKaR/pub?gid=1290048305&output=csv',
      female: 'https://docs.google.com/spreadsheets/d/e/2PACX-1vQo5-YqMdciz9mB0xICnp1_f3oyodh3Rdoykyicjfd2qlIn8fzUT9vgWNl-ERBqiyLb80Rgg6mZwKaR/pub?output=csv',
    },
    '2024': {
      male: 'https://docs.google.com/spreadsheets/d/e/2PACX-1vR0w0PDulvh5c6zLSRfKhq-0JATaIAnFVIYkizbWefHfIotzRV0x5Stm2juI6_91LdM2_MAhAYbY5ge/pub?gid=1290048305&output=csv',
      female: 'https://docs.google.com/spreadsheets/d/e/2PACX-1vR0w0PDulvh5c6zLSRfKhq-0JATaIAnFVIYkizbWefHfIotzRV0x5Stm2juI6_91LdM2_MAhAYbY5ge/pub?output=csv',
    },
    '2023': {
      male: 'https://docs.google.com/spreadsheets/d/e/2PACX-1vSj31GOuRQ2tTJ_uSQKj2L0Y5cqRceS1fw4GiHOoXVVS01RQs0MGHFqGo-EegCewKHs21ukjmiEEvdj/pub?gid=1290048305&output=csv',
      female: 'https://docs.google.com/spreadsheets/d/e/2PACX-1vSj31GOuRQ2tTJ_uSQKj2L0Y5cqRceS1fw4GiHOoXVVS01RQs0MGHFqGo-EegCewKHs21ukjmiEEvdj/pub?output=csv',
    },
  };
  return baseUrls[season]?.[gender] || baseUrls['2026']['male'];
};

const DataContext = createContext(null);

export const DataProvider = ({ children }) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const location = useLocation();
  const isHomePage = location.pathname === '/';

  const [season, setSeasonState] = useState(searchParams.get('season') || '2026');
  const [gender, setGenderState] = useState(searchParams.get('gender') || 'female');
  const [data, setData] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  // S'assure que season/gender apparaissent dans l'URL, sauf sur la page d'accueil
  // (qu'on garde volontairement propre, sans paramètres visibles).
  useEffect(() => {
    if (isHomePage) {
      // Si on arrive sur l'accueil avec d'anciens paramètres dans l'URL (lien partagé,
      // retour en arrière...), on les retire pour garder l'URL propre.
      if (searchParams.get('season') || searchParams.get('gender')) {
        const params = new URLSearchParams(searchParams);
        params.delete('season');
        params.delete('gender');
        setSearchParams(params, { replace: true });
      }
      return;
    }

    const params = new URLSearchParams(searchParams);
    let changed = false;
    if (!params.get('season')) { params.set('season', season); changed = true; }
    if (!params.get('gender')) { params.set('gender', gender); changed = true; }
    if (changed) setSearchParams(params, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isHomePage]);

  const setSeason = (value) => {
    setSeasonState(value);
    if (isHomePage) return;
    const params = new URLSearchParams(searchParams);
    params.set('season', value);
    setSearchParams(params, { replace: true });
  };

  const setGender = (value) => {
    setGenderState(value);
    if (isHomePage) return;
    const params = new URLSearchParams(searchParams);
    params.set('gender', value);
    setSearchParams(params, { replace: true });
  };

  useEffect(() => {
    const fetchData = async () => {
      setIsLoading(true);
      setError(null);
      try {
        const csvUrl = getCsvUrl(season, gender);
        const response = await axios.get(csvUrl);

        const parsed = Papa.parse(response.data, {
          header: true,
          skipEmptyLines: true,
          dynamicTyping: false,
          transformHeader: (header) => header.trim(),
          transform: (value) => (value ? value.toString().trim() : ''),
        });

        // Certaines saisons (ex: 2026) n'ont pas de colonne "Catégorie" directe sur la
        // feuille Google Sheets — elle est répartie entre "Filière" et "Cat. Age".
        // On la reconstruit ici pour que tout le reste du site (recherche, classement,
        // stats, comparateur...) puisse continuer à utiliser item['Catégorie'] partout,
        // sans avoir à gérer un cas particulier par saison dans chaque page.
        const hasCategorieColumn = parsed.meta.fields.includes('Catégorie');

        const normalizedData = hasCategorieColumn
          ? parsed.data
          : parsed.data.map((row) => ({
              ...row,
              'Catégorie': [row['Filière'], row['Cat. Age']].filter(Boolean).join(' ').trim(),
            }));

        setData(normalizedData);
      } catch (err) {
        console.error('Erreur lors du chargement du CSV :', err);
        setError("Impossible de charger les données pour le moment. Réessaie dans quelques instants.");
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, [season, gender]);

  const value = { season, setSeason, gender, setGender, data, isLoading, error };

  return <DataContext.Provider value={value}>{children}</DataContext.Provider>;
};

export const useData = () => useContext(DataContext);
